/**
 * Steering, crowd separation and collision.
 *
 * Three sources of intent, in priority order: knockback (physics wins),
 * an engagement (stand and fight), then navigation (formation slot for the
 * player, flow field for the horde). Everything else is local avoidance so a
 * 400-strong crowd behaves like a crowd rather than a single-file queue.
 */

import { MAP_H, MAP_W, MAX_UNITS } from '../constants.ts';
import { alliedWith, areHostile, TEAM_ALLIANCE, Team, Terrain, UF, UnitState } from '../types.ts';
import { companyCommanded, findCompany, OrderKind, tileBlocks, type World } from '../world.ts';
import { unitType } from '../../data/units.ts';
import { clamp } from '../../core/math.ts';
import { waveModifier } from './director.ts';
import { elevationRange, wallMoveTarget } from './garrison.ts';
import { groundSpeed } from '../field.ts';
import { formationLineClear, staysOnBridge } from './gatepath.ts';

/** Nightfall takes two tiles off what your archers can reach. */
const NIGHTFALL_RANGE = -2;
/** Ash Storm slows everything on the field, both sides alike. */
const ASHSTORM_SPEED = 0.78;

const SEPARATION_RADIUS = 0.92;
const SEPARATION_FORCE = 5.2;
const STEER_LERP = 11;
const KNOCK_DECAY = 7.5;
/** Ground travellers ease into their slot instead of coasting past and reversing. */
const ARRIVAL_RATE = 3.6;
const ARRIVAL_STOP = 0.055;

const flowOut = { x: 0, y: 0 };
const walkOut = { x: 0, y: 0 };
const wallMotion = new Uint8Array(MAX_UNITS);

export function stepMovement(w: World, dt: number): void {
  const u = w.units;
  const high = u.slots.high;
  const alive = u.slots.alive;
  wallMotion.fill(0, 0, high);

  // One lookup per tick, not one per unit.
  const ashstorm = waveModifier(w, 'ashstorm');

  // ── 1. Desired velocity ─────────────────────────────────────────────────
  for (let i = 0; i < high; i++) {
    if (alive[i] === 0) continue;
    const t = unitType(u.typeId[i]);

    if (u.stunT[i] > 0) {
      u.vx[i] *= 1 - Math.min(1, dt * 9);
      u.vy[i] *= 1 - Math.min(1, dt * 9);
      continue;
    }

    let speed = u.speed[i];
    // Ash Storm: everything moves slower, both ways. Deliberately symmetric —
    // it is weather, not a debuff, and a horde wading through it is the half
    // the player is glad of.
    if (ashstorm) speed *= ASHSTORM_SPEED;
    if (u.slowT[i] > 0) speed *= 0.5;
    if (u.rallyT[i] > 0) speed *= 1.15;
    // Tired troops are slower, and a company that has been run across the field
    // arrives in no state to fight. This is the cost of chasing routers.
    speed *= 1 - u.fatigue[i] * 0.3;
    /**
     * And the ground under his feet.
     *
     * `TERRAIN_MOVE_COST` has existed since the map did and was read by
     * `flow.ts` and `path.ts` and by **nothing else** — so terrain has always
     * decided which way a pathfinder routes and has never, once, decided how
     * fast anybody walks. A marsh on a battlefield was a colour. This is the
     * line that makes the ground mean something, together with the slope term
     * `groundSpeed` measures a tile and a bit ahead: a company charging a ridge
     * pays for the climb and gets a little of it back coming down.
     *
     * Gated on `heightGrid`, which is non-null only on a field that has had
     * relief laid on it. That is deliberately narrow. A settlement's terrain is
     * its economy — where the timber is, where the ore is — and charging the
     * villagers a speed tax for walking through the wood they are felling is a
     * different design decision made by somebody else on a different day. It
     * also means the three siege digests in `tests/fields.test.ts`, which are a
     * shared tripwire this batch, cannot move: `createWorld` never sets it.
     *
     * **And it is only charged to a man who is going somewhere.** A model that
     * has a target is already holding at reach and shuffling the last half
     * tile; charging it the marsh as well is how `npm run battleground --
     * --fight` went from 4 undecided fields in 20 to 9. The ground decides how
     * an army *arrives* — slower, later, out of breath and shot at the whole
     * way — and it does not decide whether a sword already in contact can
     * reach. Put the other way round: the ground is a tax on manoeuvre, not on
     * melee, and a rule that taxes melee produces two lines standing nineteen
     * tiles apart for three hundred seconds, which is exactly what it produced.
     */
    if (w.heightGrid !== null && u.targetH[i] < 0 && u.targetBuilding[i] < 0 && u.targetWall[i] < 0) {
      speed *= groundSpeed(w, u.x[i], u.y[i], u.vx[i], u.vy[i]);
    }
    if (t.speed === 0) speed = 0;

    let dx = 0;
    let dy = 0;
    let want = false;
    let arrival = -1;

    // Keep each walking leg on the connected deck and stop exactly at corners.
    if (wallMoveTarget(w, i, walkOut)) {
      wallMotion[i] = 1;
      const dx = walkOut.x - u.x[i], dy = walkOut.y - u.y[i];
      const distance = Math.hypot(dx, dy);
      const stride = Math.min(speed, distance / Math.max(dt, 0.0001));
      u.vx[i] = distance > 0.01 ? dx / distance * stride : 0;
      u.vy[i] = distance > 0.01 ? dy / distance * stride : 0;
      if (distance > 0.01) {
        u.facing[i] = Math.atan2(dy, dx);
        if (u.state[i] !== UnitState.Routing) u.state[i] = UnitState.Moving;
      } else {
        if (u.state[i] === UnitState.Moving) u.state[i] = UnitState.Idle;
        const tx = u.goalX[i] - u.x[i], ty = u.goalY[i] - u.y[i];
        if (tx * tx + ty * ty > 0.004) u.facing[i] = Math.atan2(ty, tx);
      }
      continue;
    }

    if (u.state[i] === UnitState.Routing) {
      // Break for the nearest edge (besiegers) or the Hearth (the home side).
      // Alliance rather than equality: a second banner fighting alongside you
      // runs to your walls, because they are the walls behind him.
      if (alliedWith(u.team[i], w.wallTeam)) {
        w.flowHome.sample(u.x[i], u.y[i], flowOut);
        dx = flowOut.x;
        dy = flowOut.y;
      } else {
        const cxr = u.x[i] - MAP_W / 2;
        const cyr = u.y[i] - MAP_H / 2;
        const l = Math.hypot(cxr, cyr) || 1;
        dx = cxr / l;
        dy = cyr / l;
      }
      want = true;
      speed *= 1.25;
    } else if (u.targetH[i] >= 0 || u.targetBuilding[i] >= 0 || u.targetWall[i] >= 0) {
      // Engaged: close to just inside reach, then hold.
      //
      // Which reach has to agree with the one `stepAttacks` swings at, or the
      // unit gets stranded in a dead band — holding at bow range while combat
      // demands melee range, so it neither shoots nor closes, forever. That is
      // exactly what an archer with an empty quiver used to do: it would stall
      // at seven tiles from a target it could no longer shoot and never draw a
      // blade, and a siege whose last invaders were dry archers could never end.
      const reach = dryMissileTroop(w, i) ? meleeReach(w, i) : engageDistance(w, i, t);
      const gx = u.goalX[i];
      const gy = u.goalY[i];
      const ddx = gx - u.x[i];
      const ddy = gy - u.y[i];
      const d = Math.hypot(ddx, ddy);
      if (d > reach) {
        dx = ddx / (d || 1);
        dy = ddy / (d || 1);
        want = true;
      } else {
        // Face the target and hold the line.
        if (d > 0.001) u.facing[i] = Math.atan2(ddy, ddx);
      }
    } else {
      const gx = u.goalX[i];
      const gy = u.goalY[i];
      const ddx = gx - u.x[i];
      const ddy = gy - u.y[i];
      const d2 = ddx * ddx + ddy * ddy;

      /**
       * Who steers themselves.
       *
       * Two conditions, and the second one used to be missing. A model
       * navigates when it is at war with the town — `w.wallTeam`, not
       * `Team.Player`, so this still means "the horde" in a settlement and
       * means "the besieger" on an assault field — *and* when nobody is
       * giving its company orders. A commanded model follows its slot, which
       * is what a formation is; without that clause an assault field would
       * have handed the player's own army to the flow field the moment it fell
       * a tile and a half behind the line, and the player would have watched a
       * retreat order be ignored.
       *
       * While a battle is still deploying nobody navigates at all: both armies
       * stand in their slots until the horn.
       */
      let navigating = areHostile(u.team[i], w.wallTeam) && w.battle?.deploying !== true;
      if (navigating) {
        // The company lookup is paid only by models that would otherwise
        // navigate, which in a settlement is the horde and nobody else. This is
        // the hottest loop in the tick and a map read per villager per frame
        // would be a real cost for an answer that is always the same.
        const cidNav = u.companyId[i];
        if (cidNav >= 0) {
          const ledNav = findCompany(w, cidNav);
          if (ledNav !== undefined && (companyCommanded(w, ledNav) ||
            (w.battle && d2 > 2.5 && !ledNav.dead && formationLineClear(w, u.x[i], u.y[i], gx, gy, u.team[i], true)))) {
            // A field company's clear formation leg is already a route. The
            // settlement flow ends at the map's Hearth marker, so sending a
            // lagging flank there pulls both its men and leashed banner inward.
            // Keep the flow fallback for blocked ground, fords and siege walls.
            navigating = false;
          }
        }
      }
      if (navigating && (u.companyId[i] < 0 || d2 > 2.5)) {
        // Long-range navigation is the flow field's job.
        const field = flowFor(w, i);
        const ok = field.sample(u.x[i], u.y[i], flowOut);
        dx = flowOut.x;
        dy = flowOut.y;
        want = ok || d2 > 0.02;
      } else if ((u.flags[i] & UF.Flying) === 0 && u.knockX[i] === 0 && u.knockY[i] === 0 && stationaryDestination(w,i)) {
        // Only direct, unengaged ground travel uses arrival steering. Flow
        // navigation, combat reach, routed flight and connected wall motion
        // keep their own rules. Scaling by distance preserves cruise speed
        // while removing the hard stop at the old 0.15-tile threshold.
        arrival = Math.sqrt(d2);
        if (arrival > ARRIVAL_STOP) {
          dx = ddx / arrival;
          dy = ddy / arrival;
          speed = Math.min(speed, arrival * ARRIVAL_RATE);
          want = true;
        }
      } else if (d2 > 0.022) {
        const d = Math.sqrt(d2);
        dx = ddx / d;
        dy = ddy / d;
        want = true;
      }
    }

    const tvx = want ? dx * speed : 0;
    const tvy = want ? dy * speed : 0;
    const k = Math.min(1, dt * STEER_LERP);
    u.vx[i] += (tvx - u.vx[i]) * k;
    u.vy[i] += (tvy - u.vy[i]) * k;

    if (arrival >= 0) {
      if (arrival <= ARRIVAL_STOP) {
        // A resting unit has no microscopic residual velocity or rotating
        // facing. Crowd separation below can still move overlapping bodies.
        u.vx[i] = 0;
        u.vy[i] = 0;
      } else {
        // A new nearby order can arrive while the unit still has cruise
        // momentum. Never spend more than the remaining gap in one tick;
        // the ordinary collision pass still authorizes the resulting step.
        const stride2 = (u.vx[i] * u.vx[i] + u.vy[i] * u.vy[i]) * dt * dt;
        if (stride2 > arrival * arrival) {
          const limit = arrival / Math.sqrt(stride2);
          u.vx[i] *= limit;
          u.vy[i] *= limit;
        }
      }
    }

    if (want && (Math.abs(u.vx[i]) > 0.02 || Math.abs(u.vy[i]) > 0.02)) {
      u.facing[i] = Math.atan2(u.vy[i], u.vx[i]);
      if (u.state[i] === UnitState.Idle) u.state[i] = UnitState.Moving;
    } else if (u.state[i] === UnitState.Moving) {
      u.state[i] = UnitState.Idle;
    }
  }

  // ── 2. Crowd separation ─────────────────────────────────────────────────
  for (let i = 0; i < high; i++) {
    if (alive[i] === 0) continue;
    if ((u.flags[i] & UF.Flying) !== 0) continue;
    // A man standing on a wall is not in the crowd. He has a stone to himself
    // and the pin above already decides where he is — leaving him in the push
    // would let the press at the foot of the wall shove him off it. It also
    // takes the whole garrison out of the hottest loop in the tick, which is
    // most of why a five-hundred-strong wall is affordable at all.
    if (wallMotion[i] !== 0) continue;
    let px = 0;
    let py = 0;
    const ri = u.radius[i];
    const xi = u.x[i];
    const yi = u.y[i];
    const teamI = u.team[i];
    /**
     * The pusher's alliance, hoisted out of the inner loop.
     *
     * This is the hottest loop in the game — every ground model against
     * everything standing near it, every tick — and the shove is 1 against your
     * own side and 1.7 against the other, which is what stops two shield lines
     * interpenetrating. Asking `alliedWith` per candidate would be two table
     * reads where one will do; the table is indexed directly here for the same
     * reason `SpatialHash.forEachCell` exists at all.
     */
    const allianceI = TEAM_ALLIANCE[teamI & 7];

    /**
     * Taken a cell at a time rather than a neighbour at a time.
     *
     * This is the single hottest loop in the simulation — every ground model,
     * every tick, against everything standing near it — and the per-neighbour
     * callback was costing as much as the arithmetic inside it. The units
     * visited, the order they are visited in and the floating-point sum are all
     * unchanged; only the number of function calls is. See
     * `SpatialHash.forEachCell`.
     *
     * Deliberately *not* capped to a fixed number of neighbours. Capping is the
     * usual boids answer and it would bound this loop properly, but separation
     * here is what stops two shield lines interpenetrating: dropping the tail
     * of a dense crowd's neighbour list is exactly the case where the push is
     * load-bearing, and it would change how a press of bodies behaves rather
     * than how long it takes to compute. The cell walk is already bounded by
     * `SEPARATION_RADIUS`, which is a little under one tile.
     */
    const r2max = SEPARATION_RADIUS * SEPARATION_RADIUS;
    w.spatial.forEachCell(xi, yi, SEPARATION_RADIUS, (items, s, e) => {
      for (let n = s; n < e; n++) {
        const j = items[n];
        if (j === i) continue;
        const dx = xi - u.x[j];
        const dy = yi - u.y[j];
        const d2 = dx * dx + dy * dy;
        if (d2 > r2max) continue;
        const minD = ri + u.radius[j];
        if (d2 > minD * minD) continue;
        const d = Math.sqrt(d2);
        if (d < 0.0005) {
          // Perfectly stacked: nudge apart deterministically by index.
          px += (i < j ? 1 : -1) * 0.5;
          py += (i < j ? -1 : 1) * 0.5;
          continue;
        }
        const overlap = (minD - d) / minD;
        // Push harder against the other side so lines don't interpenetrate.
        const weight = TEAM_ALLIANCE[u.team[j] & 7] === allianceI ? 1 : 1.7;
        const mass = (u.flags[j] & UF.Large) !== 0 ? 0.35 : 1;
        px += (dx / d) * overlap * weight * mass;
        py += (dy / d) * overlap * weight * mass;
      }
    });

    if (px !== 0 || py !== 0) {
      const selfMass = (u.flags[i] & UF.Large) !== 0 ? 0.4 : 1;
      u.vx[i] += px * SEPARATION_FORCE * selfMass * dt;
      u.vy[i] += py * SEPARATION_FORCE * selfMass * dt;
    }
  }

  // ── 3. Integrate, knockback, collide ────────────────────────────────────
  for (let i = 0; i < high; i++) {
    if (alive[i] === 0) continue;

    let mx = u.vx[i] * dt + (wallMotion[i] ? 0 : u.knockX[i] * dt);
    let my = u.vy[i] * dt + (wallMotion[i] ? 0 : u.knockY[i] * dt);

    const decay = Math.max(0, 1 - KNOCK_DECAY * dt);
    u.knockX[i] *= decay;
    u.knockY[i] *= decay;
    if (Math.abs(u.knockX[i]) < 0.01) u.knockX[i] = 0;
    if (Math.abs(u.knockY[i]) < 0.01) u.knockY[i] = 0;

    // Cap per-tick displacement so nothing tunnels through a wall.
    const maxStep = 0.45;
    const stepLen = Math.hypot(mx, my);
    if (stepLen > maxStep) {
      mx = (mx / stepLen) * maxStep;
      my = (my / stepLen) * maxStep;
    }

    const flying = (u.flags[i] & UF.Flying) !== 0;
    const dryCrossing = !flying && staysOnBridge(w, i);
    const x0 = u.x[i];
    const y0 = u.y[i];
    // Collision must inspect the Float32 coordinate the unit will actually
    // store. A double just below a deck edge can round onto the water tile.
    let nx = Math.fround(x0 + mx);
    let ny = Math.fround(y0 + my);

    if (!flying) {
      // Two open endpoints do not make a diagonal gap between touching stones.
      // Deck walkers use their own connected-wall route and per-unit access.
      const cutsCorner = wallMotion[i] === 0 && Math.floor(nx) !== Math.floor(x0) && Math.floor(ny) !== Math.floor(y0)
        && (solid(w, nx, y0, i, dryCrossing) || solid(w, x0, ny, i, dryCrossing));
      if (cutsCorner || solid(w, nx, ny, i, dryCrossing)) {
        // Slide: try each axis alone before giving up.
        if (!solid(w, nx, y0, i, dryCrossing)) {
          ny = y0;
        } else if (!solid(w, x0, ny, i, dryCrossing)) {
          nx = x0;
        } else {
          nx = x0;
          ny = y0;
        }
      }
    }

    u.x[i] = clamp(nx, 0.35, MAP_W - 0.35);
    u.y[i] = clamp(ny, 0.35, MAP_H - 0.35);
  }
}

/** A marching or turning formation slot keeps moving; it is not an arrival. */
function stationaryDestination(w: World, i: number): boolean {
  const cid = w.units.companyId[i];
  if (cid < 0) return true;
  const c = findCompany(w,cid);
  // Uncommanded armies advance from their lane or enemy selection even when
  // orderKind is None. Braking their followers makes the banner leash pull
  // companies inward and compresses an advancing army's frontage.
  return c === undefined || (companyCommanded(w,c) && c.orderKind === OrderKind.None && c.orderFacing == null);
}

/** Which flow field this unit should follow. */
function flowFor(w: World, i: number) {
  const cid = w.units.companyId[i];
  if (cid >= 0) {
    const c = findCompany(w, cid);
    if (c && c.flowKind === 1) return w.flowEcon;
  }
  return w.flowMain;
}

/**
 * A unit's reach, including any range the Sovereign has bought it — and any
 * height it happens to be standing at.
 *
 * The elevation term is deliberately only paid to things that shoot. A spearman
 * on a parapet gets nothing, which sounds like a slight and is the point: he
 * cannot reach the ground and the ground cannot reach him, so what he is there
 * for is whatever comes *up* — a ladder, a sapper, an engine. Archers see
 * further; spears hold the top of the ladder. That division is what makes the
 * composition of a garrison a decision rather than "put your best men up there".
 */
export function effRange(w: World, i: number, t = unitType(w.units.typeId[i])): number {
  // Nightfall: cold dark, and your archers see two tiles less. Yours only —
  // the horde does not need to see to know where the Hearth is.
  const dark = w.units.team[i] === Team.Player && waveModifier(w, 'nightfall') ? NIGHTFALL_RANGE : 0;
  const shoots = (w.units.flags[i] & UF.Ranged) !== 0;
  const up = shoots ? elevationRange(w, i) : 0;
  if (w.units.team[i] === Team.Player && shoots) {
    return Math.max(1, t.range + w.mods.archerRange + dark + up);
  }
  return t.range + up;
}

/** Distance at which this unit stops closing and starts swinging. */
export function engageDistance(w: World, i: number, t = unitType(w.units.typeId[i])): number {
  const u = w.units;
  let targetR = 0.3;
  const th = u.targetH[i];
  if (th >= 0) {
    const j = u.resolve(th);
    if (j >= 0) targetR = u.radius[j];
  } else if (u.targetBuilding[i] >= 0 || u.targetWall[i] >= 0) {
    targetR = 0.62;
  }
  return effRange(w, i, t) + u.radius[i] + targetR - 0.08;
}

/**
 * A missile troop that has shot its quiver dry. It has to fight like infantry
 * from here, which means it has to *stand* like infantry — see the engaged
 * branch above.
 */
export function dryMissileTroop(w: World, i: number): boolean {
  return (w.units.flags[i] & UF.Ranged) !== 0 && w.units.ammo[i] <= 0;
}

/**
 * The distance at which a swing connects. Lives here rather than in combat.ts
 * so that the steering pass and the attack pass cannot drift apart — they are
 * the same number by construction.
 */
export function meleeReach(w: World, i: number): number {
  const u = w.units;
  let targetR = 0.3;
  const j = u.resolve(u.targetH[i]);
  if (j >= 0) targetR = u.radius[j];
  else if (u.targetBuilding[i] >= 0 || u.targetWall[i] >= 0) targetR = 0.62;
  return 0.6 + u.radius[i] + targetR;
}

/**
 * Static-geometry test used by the collision pass.
 *
 * Takes the unit rather than its team, because the interesting exemption is
 * per-*unit*: a model that has been posted to a section of curtain may step onto
 * **that one tile** and onto no other. That is the whole of "climbing" — two
 * array reads and a compare, rather than stairs as objects and a second
 * navigation graph. A man cannot walk along the wall to the next stone, and he
 * cannot walk onto a stone somebody else was given; he can get onto his own,
 * from whichever side he approaches it.
 */
function solid(w: World, x: number, y: number, i: number, dryCrossing = false): boolean {
  const tx = Math.floor(x);
  const ty = Math.floor(y);
  if (tx < 0 || ty < 0 || tx >= MAP_W || ty >= MAP_H) return true;
  if (dryCrossing) {
    const t = w.map.terrain[w.map.idx(tx, ty)];
    if (t === Terrain.River || t === Terrain.Deep || t === Terrain.Ditch) return true;
  }
  // One statement of the rule, shared with the eviction at the stamp sites —
  // see `tileBlocks` in `world.ts`. If the two ever disagreed, a unit could be
  // evicted onto ground this pass refuses, and freeze there instead.
  return tileBlocks(w, tx, ty, i);
}
